import {
  FetchArgs,
  FetchBaseQueryError,
  QueryReturnValue,
} from '@reduxjs/toolkit/query';
import { CREDENTIALING_MOCKS } from '@utils/constants';

export type BaseQuery = (
  arg: string | FetchArgs
) =>
  | QueryReturnValue<unknown, FetchBaseQueryError, unknown>
  | PromiseLike<QueryReturnValue<unknown, FetchBaseQueryError, unknown>>;

const MOCK_LATENCY_MS = 300;

export type ContractOptions<Arg, Result> = {
  toServer?: (arg: Arg, baseQuery: BaseQuery) => Promise<FetchArgs>;
  fromServer?: (data: unknown, arg: Arg) => Result;
  whenMissing?: Result;
  /** answer from the mock whatever VITE_CREDENTIALING_MOCKS says, until the backend has the endpoint */
  alwaysMock?: boolean;
};

const isMissingEndpoint = (error: FetchBaseQueryError) =>
  error.status === 404 &&
  (error.data as { errorCode?: string } | undefined)?.errorCode ===
    'ENDPOINT_NOT_FOUND';

/**
 * queryFn for an endpoint whose backend contract is not live yet. `request` is
 * the agreed contract; with VITE_CREDENTIALING_MOCKS=true `mock` answers instead,
 * so components never know which one ran. A mock signals an API error by
 * throwing a FetchBaseQueryError-shaped object.
 */
export const contractQueryFn =
  <Arg, Result>(
    request: (arg: Arg) => FetchArgs,
    mock: (arg: Arg) => Result,
    {
      alwaysMock,
      fromServer,
      toServer,
      whenMissing,
    }: ContractOptions<Arg, Result> = {}
  ) =>
  async (
    arg: Arg,
    _api: unknown,
    _extraOptions: unknown,
    baseQuery: BaseQuery
  ): Promise<
    QueryReturnValue<Result, FetchBaseQueryError, Record<string, unknown>>
  > => {
    if (CREDENTIALING_MOCKS || alwaysMock) {
      await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));

      try {
        return { data: mock(arg) };
      } catch (error) {
        return { error: error as FetchBaseQueryError };
      }
    }

    let args: FetchArgs;
    try {
      args = toServer ? await toServer(arg, baseQuery) : request(arg);
    } catch (error) {
      return { error: error as FetchBaseQueryError };
    }

    const result = await baseQuery(args);

    if (result.error) {
      return whenMissing !== undefined && isMissingEndpoint(result.error)
        ? { data: whenMissing }
        : { error: result.error };
    }

    return {
      data: fromServer ? fromServer(result.data, arg) : (result.data as Result),
    };
  };

export const mockApiError = (
  status: number,
  message: string
): FetchBaseQueryError => ({ status, data: { message } });
